import { useServices } from "contexts/ServiceContext";
import { EventEnum } from "enums/EventEnum";
import { useFormik } from "formik";
import {
    NotifyUserDataMutation,
    NotifyUserDataMutationVariables,
    UserDataAction,
} from "graphql/schema-types";
import useGQLOperation from "hooks/useGQLOperation";
import { useCallback, useMemo, useState } from "react";
import { Analytics } from "services/Analytics/Analytics";
import notifyUserData from "services/gql/mutations/notifyUserData";
import styled from "styled-components";
import useTranslation from "translations/useTranslation";
import Button, { Variants } from "ui/components/Button/Button";
import Input from "ui/components/Input/Input";
import * as Yup from "yup";

const StyledSectionTitle = styled.h2`
    font-size: ${(props) => props.theme.typography.heading.normal.fontSize};
    line-height: ${(props) => props.theme.typography.heading.normal.lineHeight};
    font-weight: ${(props) => props.theme.typography.heading.normal.fontWeight};
    color: ${(props) => props.theme.colors.neutral.neutral800};
`;

const Paragraph = styled.p`
    max-width: 600px;
    margin: ${(props) => props.theme.spacing.md} 0;
    font-size: ${(props) => props.theme.typography.body.normal.fontSize};
    line-height: ${(props) => props.theme.typography.body.normal.lineHeight};
    font-weight: ${(props) => props.theme.typography.body.normal.fontWeight};
    color: ${(props) => props.theme.colors.neutral.neutral400};
`;

const ButtonWrapper = styled.div`
    display: flex;
    gap: ${(props) => props.theme.spacing.sm};
`;

const StyledSection = styled.section`
    margin-bottom: ${(props) => props.theme.spacing.md};
    max-width: 600px;
`;

const InputWrapper = styled.div`
    margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const eventMap = {
    [UserDataAction.Extract]: EventEnum.ExtractData,
    [UserDataAction.Delete]: EventEnum.DeleteData,
};

enum ResultMessageType {
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
}

type ResultMessage = {
    type: ResultMessageType;
    message: string;
};

export default function PrivacyManagement() {
    const t = useTranslation();
    const services = useServices();

    const [resultMessage, setResultMessage] = useState<ResultMessage>();

    const [submitForm] = useGQLOperation<NotifyUserDataMutation, NotifyUserDataMutationVariables>(
        notifyUserData,
    );

    const mailErrorMessage = t("MAIL_ERROR");

    const initialValues = {};
    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                email: Yup.string().email(mailErrorMessage).required(mailErrorMessage),
            }),
        [mailErrorMessage],
    );

    const handleSubmit = useCallback(
        async (values) => {
            if (!values.action) return;

            try {
                const response = await submitForm({
                    action: values.action,
                    mail: values.email,
                });

                if (response.NotifyUserData) {
                    setResultMessage({
                        type: ResultMessageType.SUCCESS,
                        message: t("MAIL_SENT"),
                    });
                } else {
                    setResultMessage({
                        type: ResultMessageType.ERROR,
                        message: mailErrorMessage,
                    });
                }
            } catch (error) {
                services.Logger.error(error);

                setResultMessage({
                    type: ResultMessageType.ERROR,
                    message: t("GENERIC_ERROR_MESSAGE"),
                });
            } finally {
                Analytics.Instance.sendEvent(eventMap[values.action]);
            }
        },
        [mailErrorMessage, services.Logger, submitForm, t],
    );

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
    });

    const helperText = useMemo(() => {
        if (formik.errors?.email) {
            return formik.errors?.email as string;
        }

        if (resultMessage) {
            return resultMessage.message;
        }

        return undefined;
    }, [formik.errors?.email, resultMessage]);

    return (
        <StyledSection>
            <StyledSectionTitle>{t("PRIVACY_MANAGEMENT")}</StyledSectionTitle>
            <Paragraph>{t("WEB_NAVIGATION_DATA_DESCRIPTION")}</Paragraph>
            <form onSubmit={formik.handleSubmit}>
                <InputWrapper>
                    <Input
                        placeholder={t("INSERT_MAIL")}
                        name="email"
                        type="text"
                        value={formik.values.email}
                        onChange={(e) => {
                            formik.handleChange(e);

                            // only reset result message state when it is set
                            if (resultMessage) {
                                setResultMessage(undefined);
                            }
                        }}
                        error={
                            !!formik.errors?.email ||
                            resultMessage?.type === ResultMessageType.ERROR
                        }
                        success={resultMessage?.type === ResultMessageType.SUCCESS}
                        helperText={helperText}
                    />
                </InputWrapper>
                <ButtonWrapper>
                    <Button
                        type="button"
                        variant={Variants.SOLID}
                        color="secondary"
                        size="md"
                        fullWidth
                        onClick={() => {
                            formik.setFieldValue("action", UserDataAction.Extract, false);
                            formik.handleSubmit();
                        }}
                    >
                        {t("EXTRACT_DATA")}
                    </Button>
                    <Button
                        type="button"
                        variant={Variants.SOLID}
                        color="secondary"
                        size="md"
                        fullWidth
                        onClick={() => {
                            formik.setFieldValue("action", UserDataAction.Delete, false);
                            formik.handleSubmit();
                        }}
                    >
                        {t("DELETE_DATA")}
                    </Button>
                </ButtonWrapper>
            </form>
        </StyledSection>
    );
}
